<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                'This cookie is used to distinguish between humans and bots. This is beneficial for the website, in order to make valid reports on the use of their website.',
            expiry: '180 days',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: "Stores the user's cookie consent state for the current domain.",
            expiry: '1 year',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose: "Used by the website's content management system (CMS) to determine how the website's menu-tabs should be displayed.",
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose: "Used by the website's content management system (CMS) to determine how the website's menu-tabs should be displayed.",
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose:
                'This cookie is used to distinguish between humans and bots. This is beneficial for the website, in order to make valid reports on the use of their website.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'This cookie is used to distinguish between humans and bots.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'This cookie is used to distinguish between humans and bots.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'This cookie is used to distinguish between humans and bots.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose:
                "Determines the preferred language of the visitor. Allows the website to set the preferred language upon the visitor's re-entry.",
            expiry: '1 year',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                "Used to send data to Google Analytics about the visitor's device and behavior. Tracks the visitor across devices and marketing channels.",
            expiry: '2 years',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                "Used to send data to Google Analytics about the visitor's device and behavior. Tracks the visitor across devices and marketing channels.",
            expiry: '2 years',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Determines the preferred language of the visitor.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
};
</script>

<template>
    <ContentPage title="Cookie Policy">
        <p>
            This website uses cookies. We use cookies to personalise content and ads, to provide social media features and to analyse our traffic. We
            also share information about your use of our site with our social media, advertising and analytics partners who may combine it with other
            information that you’ve provided to them or that they’ve collected from your use of their services.
        </p>
        <p>Cookies are small text files that can be used by websites to make a user's experience more efficient.</p>
        <p>
            The law states that we can store cookies on your device if they are strictly necessary for the operation of this site. For all other types
            of cookies we need your permission.
        </p>
        <p>This site uses different types of cookies. Some cookies are placed by third party services that appear on our pages.</p>
        <p>You can at any time change or withdraw your consent from the Cookie Declaration on our website.</p>
        <p>
            Learn more about who we are, how you can contact us and how we process personal data in our
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Privacy Policy</NuxtLinkLocale>.
        </p>
        <p>Please state your consent ID and date when you contact us regarding your consent.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Necessary (8)</h3>
            <p>
                Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the
                website. The website cannot function properly without these cookies.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Preferences (1)</h3>
            <p>
                Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred
                language or the region that you are in.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the
                individual user and thereby more valuable for publishers and third party advertisers.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Unclassified (1)</h3>
            <p>Unclassified cookies are cookies that we are in the process of classifying, together with the providers of individual cookies.</p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
